import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import queryClient from '../../utils/queryClient';
import UserAPI, {
  type ChangeUnverifiedEmailResult,
  type SendVerificationCodeResult,
  type VerificationStatus,
  type ChangePasswordPayload,
  type ExchangeTransferTokenPayload,
  type FacebookLoginPayload,
  type ForgotPasswordPayload,
  type GoogleLoginPayload,
  type PasswordLoginPayload,
  type RegisterPayload,
  type ResetPasswordPayload,
  type UpdateProfilePayload,
} from '../fetch/userApi';
import notify from '../../utils/notify';
import { orShopNotOpen } from '../../utils/shopNotOpen';
import { isApiError, type ApiError } from '../fetch/apiClient';
import { isMfaRequired } from '../types';
import { readImpersonation } from '../../utils/jwt';
import type {
  AccountInfo,
  AuthProvidersResponse,
  AuthTokenResponse,
  User,
} from '../types';
import type { Locale } from '../../i18n/locales';

export const TOKEN_KEY = 'token';
export const GUEST_TOKEN_KEY = 'guest_token';

/**
 * Where the admin's own token is parked while impersonating. It MUST be
 * written before the impersonate response lands: the global `newToken`
 * response callback overwrites TOKEN_KEY the instant any response body
 * carrying a `token` arrives.
 */
export const ADMIN_TOKEN_KEY = 'admin_token';

export interface FlattenedUser extends User {
  token: string;
}

export const login = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
  queryClient.invalidateQueries({ queryKey: [] });
};

export const logout = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  // Also drop any stashed admin token: leaving a stale (if long-lived) admin
  // credential behind after a hard logout is its own credential-hygiene bug,
  // independent of whichever path got here.
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  queryClient.invalidateQueries({ queryKey: ['login'], refetchType: 'active' });
  queryClient.invalidateQueries({ queryKey: [], refetchType: 'none' });
};

// Only pass through values that are actually i18n keys (dotted, e.g.
// `notify.impersonation.target_not_found`). Some failures — notably
// CanCan::AccessDenied, rendered by application_controller_base.rb as
// `{ error: "cannot" }` — carry a bare, non-key word that i18next would
// otherwise echo back verbatim (no translation, no defaultValue) as a raw
// "cannot" toast. Anything that isn't key-shaped falls back to the
// caller-supplied default instead.
const isI18nKey = (value: string): boolean => value.includes('.');

const errorMessageFromApi = (error: unknown, fallback: string): string => {
  if (isApiError(error)) {
    const body = error.body as { error?: string; resp?: string } | undefined;
    if (typeof body?.error === 'string' && isI18nKey(body.error)) return body.error;
    if (typeof body?.resp === 'string' && isI18nKey(body.resp)) return body.resp;
  }
  return fallback;
};

// A token the server rejects outright is not an error to retry — it means
// "not logged in": revoked fleet-wide, signed by another instance's key, or
// long expired. Left in the row as an error, /users/me was refetched by
// every observer that mounted, and components gated on isPending
// (ProtectedRoute and friends) mount ↔ unmount with each attempt — a
// ~100 req/s loop that never settled. Drop the token and let the row resolve
// to logged out — or, mid-impersonation, hand the admin back their own token
// and return it, so the caller can fetch the admin's row in the same pass.
//
// A closed shop rejects its customers' sessions the same way (401). The
// cached account info usually still says the shop is open, so refetch it
// (anonymously now): when it says closed, MainLayout shows ShopClosedPage
// and "your session has expired" would be the wrong explanation.
const dropRejectedToken = async (client: QueryClient, token: string): Promise<string | null> => {
  if (readImpersonation(token)) {
    const stashed = localStorage.getItem(ADMIN_TOKEN_KEY);
    exitImpersonationLocally();
    notify('notify.warning', 'notify.impersonation.ended', { type: 'warning' });
    return stashed;
  }
  localStorage.removeItem(TOKEN_KEY);
  // Join a refetch already in flight: another rejected request's run may
  // have started one, and cancelling it would resolve this await early.
  await client.invalidateQueries({ queryKey: ['accountInfo'], refetchType: 'all' }, { cancelRefetch: false });
  const shopClosed = client
    .getQueriesData<AccountInfo>({ queryKey: ['accountInfo'] })
    .some(([, info]) => info?.shop_state === 'closed');
  if (!shopClosed) notify('notify.warning', 'notify.session.expired', { type: 'warning' });
  return null;
};

export const useUserData = () => {
  const client = useQueryClient();
  return useQuery<FlattenedUser | null>({
    queryKey: ['login'],
    queryFn: async () => {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return null;
      try {
        const meResult = await UserAPI.getMe();
        return { ...meResult, token };
      } catch (error) {
        if (isApiError(error) && error.status === 401) {
          const restored = await dropRejectedToken(client, token);
          if (!restored) return null;
          const meResult = await UserAPI.getMe();
          return { ...meResult, token: restored };
        }
        throw error;
      }
    },
    // Other failures (network down, 5xx) stay errors, but a mounting observer
    // must not refetch them — that is the same mount ↔ unmount loop as above.
    // Focus, reconnect and explicit invalidation still refetch.
    retryOnMount: false,
  });
};

export const useAuthProviders = (email: string) =>
  useQuery<AuthProvidersResponse>({
    queryKey: ['authProviders', email],
    queryFn: () => UserAPI.getAuthProviders({ email }),
  });

export const usePasswordLogin = () =>
  useMutation<AuthTokenResponse, ApiError, PasswordLoginPayload>({
    mutationFn: UserAPI.loginWithPassword,
    onSuccess: (data) => {
      // login(data.token) handled automatically by the newToken callback
      if (!isMfaRequired(data)) localStorage.removeItem(GUEST_TOKEN_KEY);
    },
    onError: (error) => {
      notify('notify.error', orShopNotOpen(error, 'notify.users.invalid_password'), { type: 'warning' });
    },
  });

export const useFacebookLogin = () =>
  useMutation<AuthTokenResponse, ApiError, FacebookLoginPayload>({
    mutationFn: UserAPI.loginWithFacebook,
    onSuccess: () => {
      localStorage.removeItem(GUEST_TOKEN_KEY);
    },
    onError: (error) => {
      notify('notify.error', errorMessageFromApi(error, 'notify.auth.invalid_facebook_login'), {
        type: 'danger',
      });
    },
  });

export const useGoogleLogin = () =>
  useMutation<AuthTokenResponse, ApiError, GoogleLoginPayload>({
    mutationFn: UserAPI.loginWithGoogle,
    onSuccess: () => {
      localStorage.removeItem(GUEST_TOKEN_KEY);
    },
    onError: (error) => {
      notify('notify.error', errorMessageFromApi(error, 'notify.auth.invalid_google_login'), {
        type: 'danger',
      });
    },
  });

export const useRegister = () =>
  useMutation<AuthTokenResponse, ApiError, RegisterPayload>({
    mutationFn: UserAPI.register,
    onSuccess: () => {
      localStorage.removeItem(GUEST_TOKEN_KEY);
    },
    onError: (error) => {
      notify('notify.error', errorMessageFromApi(error, 'notify.auth.registration_failed'), {
        type: 'danger',
      });
    },
  });

// Deliberately no onError notification: the ForgotPassword page shows the
// same confirmation whether or not the address exists, so surfacing a
// failure here would leak which addresses have accounts.
export const useForgotPassword = () =>
  useMutation<void, ApiError, ForgotPasswordPayload>({
    mutationFn: UserAPI.forgotPassword,
  });

export const useResetPassword = () =>
  useMutation<void, ApiError, ResetPasswordPayload>({
    mutationFn: UserAPI.resetPassword,
    onError: (error) => {
      notify('notify.error', errorMessageFromApi(error, 'notify.auth.password_reset_failed'), {
        type: 'danger',
      });
    },
  });

// The session token carries email_verified (pic-service's album
// subscriptions read it), so a logged-in user gets a fresh one; the user
// query refetches either way, for the profile's verified state.
const refreshSessionAfterVerification = async (client: QueryClient): Promise<void> => {
  if (localStorage.getItem(TOKEN_KEY)) {
    await UserAPI.refreshToken().catch(() => undefined);
  }
  // Awaited: callers navigate on success, and a guard reading the still-cached
  // user would send them straight back.
  await client.invalidateQueries({ queryKey: ['login'] });
};

export const useVerifyEmail = () => {
  const client = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: UserAPI.verifyEmail,
    onSuccess: () => refreshSessionAfterVerification(client),
  });
};

// The code page (GET /auth/verification-status). Never cached: the page needs
// the live "is a code active / how long until a resend" answer on every visit.
export const useVerificationStatus = (enabled: boolean) =>
  useQuery<VerificationStatus>({
    queryKey: ['verificationStatus'],
    queryFn: UserAPI.verificationStatus,
    enabled,
    staleTime: 0,
    gcTime: 0,
  });

export const useSendVerificationCode = () =>
  useMutation<SendVerificationCodeResult, ApiError, void>({
    mutationFn: UserAPI.sendVerificationCode,
  });

export const useVerifyEmailCode = () => {
  const client = useQueryClient();
  return useMutation<{ email_verified: boolean }, ApiError, string>({
    mutationFn: UserAPI.verifyEmailCode,
    onSuccess: async () => {
      // The code was the caller's own: mark the cached session verified at once,
      // so no guard acts on the stale value while the refetch below is in flight.
      client.setQueryData<FlattenedUser | null | undefined>(['login'], (u) =>
        u ? { ...u, email_verified: true } : u,
      );
      await refreshSessionAfterVerification(client);
    },
  });
};

export const useChangeUnverifiedEmail = () => {
  const client = useQueryClient();
  return useMutation<ChangeUnverifiedEmailResult, ApiError, string>({
    mutationFn: UserAPI.changeUnverifiedEmail,
    onSuccess: (result) => {
      client.setQueryData<VerificationStatus | undefined>(['verificationStatus'], (old) =>
        // The change killed the old address's code: one is live only if the
        // send for the new address went out.
        old ? { ...old, email: result.email, code_active: result.sent } : old,
      );
      void client.invalidateQueries({ queryKey: ['login'] });
    },
  });
};

export const useResendVerification = () =>
  useMutation<void, ApiError, string>({
    mutationFn: UserAPI.resendVerification,
    onSuccess: () => {
      notify('notify.success', 'notify.auth.verification_sent');
    },
    onError: (error) => {
      notify('notify.error', errorMessageFromApi(error, 'notify.auth.verification_send_failed'), {
        type: 'danger',
      });
    },
  });

export const useRefreshToken = () =>
  useMutation<AuthTokenResponse, ApiError, void>({
    mutationFn: UserAPI.refreshToken,
    onError: () => {
      // A rejected refresh during impersonation means the session was closed,
      // the target was disabled, or the actor lost their rights. Hand the
      // admin back their own session rather than logging them out.
      if (readImpersonation(localStorage.getItem(TOKEN_KEY))) {
        exitImpersonationLocally();
        notify('notify.warning', 'notify.impersonation.ended', { type: 'warning' });
      } else {
        logout();
      }
    },
  });

interface StartImpersonationContext {
  // True only when this call's onMutate actually wrote ADMIN_TOKEN_KEY.
  // Distinguishes "we created this stash, so we own cleaning it up on
  // failure" from "someone else's stash was already here" — see onError.
  stashed: boolean;
}

export const useStartImpersonation = () =>
  useMutation<unknown, ApiError, { user_id: string }, StartImpersonationContext>({
    mutationFn: UserAPI.startImpersonation,
    onMutate: () => {
      const current = localStorage.getItem(TOKEN_KEY);
      // A stray re-entrant start — fired while TOKEN_KEY already holds an
      // impersonation token, e.g. a stale UI still offering "impersonate"
      // mid-session — must never overwrite the real admin token an earlier
      // start already stashed. The backend rejects re-entrant impersonation
      // outright (ability.rb: `cannot :impersonate` when already
      // impersonating), so the only thing that matters client-side is that
      // this call degrades to a no-op instead of destroying the one stash
      // that lets the admin get back.
      if (current && !readImpersonation(current)) {
        localStorage.setItem(ADMIN_TOKEN_KEY, current);
        return { stashed: true };
      }
      return { stashed: false };
    },
    onError: (error, _variables, context) => {
      // Only remove a stash THIS call created. If onMutate skipped the
      // stash (re-entrant case), context.stashed is false and the earlier
      // stash — the admin's only way back — must survive the failure.
      if (context?.stashed) localStorage.removeItem(ADMIN_TOKEN_KEY);
      // Surface the server's specific key (target_not_found, not_available,
      // ...) when the response body carries one, falling back to a generic
      // message — same pattern as the other mutations in this file.
      notify('notify.error', errorMessageFromApi(error, 'notify.impersonation.start_failed'), {
        type: 'warning',
      });
    },
  });

/**
 * Ends impersonation. The response carries the admin's fresh token, which the
 * global newToken callback installs. If the call fails we fall back to the
 * stashed copy so the admin is never stranded inside an impersonated session.
 */
export const useStopImpersonation = () =>
  useMutation<unknown, ApiError, void>({
    mutationFn: UserAPI.stopImpersonation,
    onSuccess: () => {
      localStorage.removeItem(ADMIN_TOKEN_KEY);
    },
    onError: () => {
      exitImpersonationLocally();
    },
  });

/**
 * Local-only exit: restores the stashed admin token without calling the
 * server. Used when the impersonation token is already dead (expired, or
 * refresh rejected), where the stop endpoint would just 401.
 */
export const exitImpersonationLocally = (): void => {
  const stashed = localStorage.getItem(ADMIN_TOKEN_KEY);
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  if (stashed) login(stashed);
  else logout();
};

export const useLogout = () =>
  useMutation<void, ApiError, void>({
    mutationFn: async () => {
      logout();
    },
  });

export const useUpdateProfile = () =>
  useMutation<User, ApiError, UpdateProfilePayload>({
    mutationFn: UserAPI.updateMe,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['login'] });
      notify('notify.success', 'profile.success.name_updated', { type: 'success' });
    },
    onError: (error) => {
      notify('notify.error', errorMessageFromApi(error, 'profile.error.generic'), {
        type: 'danger',
      });
    },
  });

/**
 * The staff "Email me reminders" switch. Its own mutation rather than a
 * `useUpdateProfile` call because that one always toasts
 * `profile.success.name_updated`, which would be a lie here.
 */
export const useUpdateReminderEmails = () =>
  useMutation<User, ApiError, boolean>({
    mutationFn: (reminder_emails: boolean) => UserAPI.updateMe({ reminder_emails }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['login'] });
      notify('notify.success', 'profile.success.reminder_emails_updated', { type: 'success' });
    },
    onError: (error) => {
      notify('notify.error', errorMessageFromApi(error, 'profile.error.generic'), { type: 'danger' });
    },
  });

export const useChangePassword = () =>
  useMutation<AuthTokenResponse, ApiError, ChangePasswordPayload>({
    mutationFn: UserAPI.changePassword,
    onSuccess: (data) => {
      if (data?.idtoken) {
        login(data.idtoken);
      }
      notify('notify.success', 'profile.success.password_changed', { type: 'success' });
    },
    onError: (error) => {
      notify('notify.error', errorMessageFromApi(error, 'profile.error.generic'), {
        type: 'danger',
      });
    },
  });

/**
 * Persists the language pick on the user's row — registered users AND guests,
 * so the choice survives the guest→account conversion at registration.
 * Invalidates ['login'] so LocaleProvider sees the stored value.
 *
 * Optimistic update: LocaleProvider ranks the server's `user.locale` ABOVE
 * the local `f2m_locale` choice when reconciling (a stored preference
 * legitimately outranks a one-off local pick — e.g. syncing across devices).
 * Without patching the cached ['login'] row here, that ranking would make
 * LocaleProvider revert an explicit pick back to the old server value for
 * the entire round trip of this PUT — worse, since the PUT itself only lands
 * once the request is built, that stale server value could shadow the pick
 * before the network call even goes out. Setting the cache immediately makes
 * the pick authoritative the instant the user clicks, not after the PUT
 * resolves. Deliberately no onError toast: a failed background sync must not
 * raise anything at the user (ruling on this branch) — onError only rolls the
 * cache back to what it held before the optimistic write.
 *
 * Invalidation lives in onSettled, not onSuccess: with two rapid picks A then
 * B, B's onMutate snapshots A's still-optimistic cache value as ITS
 * "previous". If A later settles (e.g. fails) after B has already succeeded
 * and invalidated, A's onError writes A's stale snapshot back over the
 * cache — and if invalidation only ran on B's success, nothing re-syncs
 * after that, leaving the cache (and, through LocaleProvider, the visible
 * language and f2m_locale) on a value that is neither the last pick nor the
 * stored row. Running invalidation in onSettled means EVERY mutation —
 * whichever of A/B settles last, success or error — ends with a refetch
 * against the real row, so a stale rollback like that one is always
 * corrected by the next tick rather than left standing.
 */
export const useSetUserLocale = () =>
  useMutation<{ locale: Locale }, ApiError, Locale, { previous: FlattenedUser | null | undefined }>({
    mutationFn: UserAPI.setLocale,
    onMutate: async (locale) => {
      await queryClient.cancelQueries({ queryKey: ['login'] });
      const previous = queryClient.getQueryData<FlattenedUser | null>(['login']);
      queryClient.setQueryData<FlattenedUser | null>(['login'], (old) =>
        old ? { ...old, locale } : old,
      );
      return { previous };
    },
    onError: (_error, _locale, context) => {
      if (context) queryClient.setQueryData(['login'], context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['login'] });
    },
  });

export const useExchangeTransferToken = () =>
  useMutation<AuthTokenResponse, ApiError, ExchangeTransferTokenPayload>({
    mutationFn: UserAPI.exchangeTransferToken,
    onSuccess: () => {
      localStorage.removeItem(GUEST_TOKEN_KEY);
    },
  });
